/**
 * Content Management Component
 * Superuser-only interface for managing RLC methodology content.
 *
 * Features:
 * - Folder-based organization
 * - Multi-file drag-n-drop upload
 * - Batch delete operations
 * - Document details drawer
 * - Tree and table views
 */

import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Button,
  App,
  Modal,
  Form,
  Input,
  Upload,
  Drawer,
  Descriptions,
  Tag,
  Space,
  Divider,
  Select,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  InboxOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../../context/AuthContext';
import contentManagementService from '../../services/contentManagementService';
import {
  createAuthStateHandler,
  buildApiUrl,
  createAuthHeaders,
} from '../../utils/authUtils';
import logger from '../../utils/logger';
import ContentManagementTreeView from './ContentManagementTreeView';
import PDFViewerCore from '../common/PDFViewerCore';
import PPTXViewer from '../common/PPTXViewer';
import OfficeDocumentViewer from '../common/OfficeDocumentViewer';

const { Dragger } = Upload;
const { TextArea } = Input;

const ContentManagement = () => {
  const { message, modal } = App.useApp();
  const {
    currentUser,
    logout,
    isTokenExpired,
    validateSession,
  } = useContext(AuthContext);

  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadForm] = Form.useForm();
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [sortBy, setSortBy] = useState('alphabetical'); // 'alphabetical' or 'date'
  const [isEditingFilename, setIsEditingFilename] = useState(false);
  const [editedFilename, setEditedFilename] = useState('');
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editedTags, setEditedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [textContent, setTextContent] = useState(null);

  // Create auth state handler
  const authHandler = useMemo(() => createAuthStateHandler(setError, logout), [logout]);

  // Check if user is authorized (super admin only)
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // Sort documents
  const sortedDocuments = useMemo(() => {
    const docs = [...documents];
    if (sortBy === 'alphabetical') {
      return docs.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortBy === 'date') {
      return docs.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
    }
    return docs;
  }, [documents, sortBy]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    await authHandler.safeApiCall(async () => {
      setError(null);

      try {
        const response = await contentManagementService.listDocuments();
        setDocuments(response.documents || []);
      } catch (err) {
        if (!authHandler.handleAuthError(err)) {
          throw new Error('Failed to fetch documents');
        }
      }
    });
  }, [authHandler]);

  // Fetch available tags
  const fetchTags = useCallback(async () => {
    await authHandler.safeApiCall(async () => {
      try {
        const response = await contentManagementService.getAllTags();
        setAvailableTags(response.tags || []);
      } catch (err) {
        // Silently fail for tags - not critical
        logger.warn('Failed to fetch tags:', err);
      }
    });
  }, [authHandler]);

  // Validate session on component mount
  useEffect(() => {
    if (!validateSession()) {
      return;
    }

    if (isSuperAdmin) {
      fetchDocuments();
      fetchTags();
    }
  }, [isSuperAdmin, validateSession, fetchDocuments, fetchTags]);

  // Handle document selection
  const handleDocumentSelect = (documentId, checked) => {
    setSelectedDocuments(prev => {
      if (checked) {
        return [...prev, documentId];
      } else {
        return prev.filter(id => id !== documentId);
      }
    });
  };


  // Handle document click (open details drawer)
  const handleDocumentClick = async (doc) => {
    setSelectedDocument(doc);
    setDetailsDrawerOpen(true);
    setDocumentUrl(null); // Reset URL
    setTextContent(null); // Reset text content
    setLoadingUrl(true);

    try {
      // Get document download URL from backend
      const response = await contentManagementService.getDocumentSource(doc.id);
      let downloadUrl = response.download_url;

      // If URL is relative (starts with /), it's a local file - prepend API base URL
      if (downloadUrl.startsWith('/')) {
        downloadUrl = buildApiUrl(downloadUrl);
      }

      setDocumentUrl(downloadUrl);

      // For text files, fetch the content
      if (doc.file_type === 'txt') {
        const authHeaders = createAuthHeaders();
        if (authHeaders) {
          const textResponse = await fetch(downloadUrl, {
            headers: authHeaders
          });
          if (textResponse.ok) {
            const text = await textResponse.text();
            setTextContent(text);
          }
        }
      }
    } catch (err) {
      message.error('Failed to load document preview');
      logger.error('Failed to get document URL:', err);
    } finally {
      setLoadingUrl(false);
    }
  };

  // Handle saving tag edits
  const handleSaveTags = async () => {
    try {
      await contentManagementService.updateDocument(selectedDocument.id, {
        tags: editedTags
      });

      // Update local state
      setSelectedDocument(prev => ({ ...prev, tags: editedTags }));
      setDocuments(prev =>
        prev.map(doc =>
          doc.id === selectedDocument.id ? { ...doc, tags: editedTags } : doc
        )
      );

      // Refresh available tags to include any newly created tags
      await fetchTags();

      setIsEditingTags(false);
      message.success('Tags updated successfully');
    } catch (err) {
      message.error('Failed to update tags');
      logger.error('Failed to update tags:', err);
    }
  };

  // Handle multi-file upload
  const handleUpload = async (values) => {
    if (uploadFiles.length === 0) {
      message.error('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    try {
      for (const fileObj of uploadFiles) {
        const formData = new FormData();
        formData.append('file', fileObj.file);
        formData.append('title', fileObj.title || fileObj.file.name.replace(/\.[^/.]+$/, ''));
        formData.append('folder_path', values.folder_path || '/');
        formData.append('category', values.category);  // Required for embeddings

        if (values.description) {
          formData.append('description', values.description);
        }

        if (values.tags && values.tags.length > 0) {
          formData.append('tags', JSON.stringify(values.tags));
        }

        try {
          await contentManagementService.uploadDocument(formData);
          successCount++;
        } catch (err) {
          failCount++;
          errors.push({ filename: fileObj.file.name, error: err.message });
        }
      }

      if (successCount > 0) {
        message.success(`Successfully uploaded ${successCount} document(s)`);
        setUploadModalOpen(false);
        uploadForm.resetFields();
        setUploadFiles([]);
        await fetchDocuments();
      }

      if (failCount > 0) {
        message.error(`Failed to upload ${failCount} document(s)`);
        console.error('Upload errors:', errors);
      }
    } finally {
      setUploading(false);
    }
  };

  // Handle batch delete
  const handleBatchDelete = () => {
    if (selectedDocuments.length === 0) {
      message.warning('No documents selected');
      return;
    }

    // Use modal confirmation pattern for destructive actions
    modal.confirm({
      title: 'Delete Selected Documents',
      content: `Are you sure you want to delete ${selectedDocuments.length} selected document(s)? This will delete all embeddings and cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await contentManagementService.batchDeleteDocuments(selectedDocuments);

          if (response.successful > 0) {
            message.success(`Successfully deleted ${response.successful} document(s)`);
            setSelectedDocuments([]);
            await fetchDocuments();
          }

          if (response.failed > 0) {
            message.warning(`Failed to delete ${response.failed} document(s)`);
            logger.error('Delete errors:', response.errors);
          }
        } catch (err) {
          message.error(err.message || 'Failed to delete documents');
        }
      },
    });
  };

  // Handle folder delete
  const handleFolderDelete = async (folderPath) => {
    if (folderPath === '/') {
      message.error('Cannot delete root folder');
      return;
    }

    // Count documents in folder
    const docsInFolder = documents.filter(
      doc => doc.folder_path === folderPath || doc.folder_path?.startsWith(folderPath + '/')
    );

    modal.confirm({
      title: `Delete folder "${folderPath}"?`,
      content: `This will delete ${docsInFolder.length} document(s) and all their embeddings. This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await contentManagementService.deleteFolder(folderPath);

          if (response.documents_deleted > 0) {
            message.success(`Successfully deleted folder: ${response.documents_deleted} documents removed`);
            setSelectedDocuments([]);
            await fetchDocuments();
          }

          if (response.documents_failed > 0) {
            message.warning(`Failed to delete ${response.documents_failed} document(s)`);
            logger.error('Delete errors:', response.errors);
          }
        } catch (err) {
          message.error(err.message || 'Failed to delete folder');
        }
      },
    });
  };

  // Handle download
  const handleDownload = async (documentId, title) => {
    try {
      const response = await contentManagementService.getDocumentSource(documentId);
      window.open(response.download_url, '_blank');
      message.success(`Downloading ${response.filename}`);
    } catch (err) {
      message.error('Failed to get download URL');
    }
  };

  // Handle cleanup stale locks
  const handleCleanupLocks = async () => {
    try {
      const response = await contentManagementService.cleanupStaleLocks();
      message.success(response.message || 'Cleanup completed');
      if (response.locks_reset > 0) {
        await fetchDocuments();
      }
    } catch (err) {
      message.error('Failed to cleanup locks');
    }
  };

  // Session expired state
  if (isTokenExpired) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>⚠️ Session Expired</h2>
        <p>Your session has expired. Please log in again to continue.</p>
        <Space>
          <Button
            type="primary"
            onClick={() => window.location.href = '/login'}
          >
            Log In Again
          </Button>
          <Button onClick={() => window.location.href = '/'}>
            Go to Home
          </Button>
        </Space>
      </div>
    );
  }

  // Check authorization
  if (!isSuperAdmin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>🔒 Access Denied</h2>
        <p>Only super administrators can access content management.</p>
        <Button type="primary" onClick={() => window.location.href = '/'}>
          Go to Home
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <p style={{ margin: '0 0 16px 0', color: 'var(--rlc-text-secondary)', fontSize: '14px' }}>
          Manage the knowledge base that the AI Coach has access to
        </p>

        {/* Sticky toolbar with statistics */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'var(--rlc-surface)',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid var(--rlc-surface-muted)',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Space size="large">
              <span><strong>{documents.length}</strong> documents</span>
              <span><strong>{selectedDocuments.length}</strong> selected</span>
              <span><strong>{documents.reduce((sum, d) => sum + (d.embedding_count || 0), 0)}</strong> embeddings</span>
              <Select
                value={sortBy}
                onChange={setSortBy}
                style={{ width: 140 }}
                options={[
                  { value: 'alphabetical', label: 'Sort: A-Z' },
                  { value: 'date', label: 'Sort: Date' },
                ]}
              />
            </Space>
            <Space>
              <Tooltip title="Reset stale locks (older than 30 minutes) on documents stuck in processing, updating, or deleting states">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleCleanupLocks}
                >
                  Cleanup Locks
                </Button>
              </Tooltip>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={selectedDocuments.length === 0}
                onClick={handleBatchDelete}
              >
                Delete Selected ({selectedDocuments.length})
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setUploadModalOpen(true)}
              >
                Upload Documents
              </Button>
            </Space>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--rlc-state-error-bg)', border: '1px solid var(--rlc-state-error-bg)', borderRadius: '4px', color: 'var(--rlc-state-error)' }}>
          {error}
        </div>
      )}

      <ContentManagementTreeView
        documents={sortedDocuments}
        selectedDocuments={selectedDocuments}
        onDocumentSelect={handleDocumentSelect}
        onDocumentClick={handleDocumentClick}
        onFolderDelete={handleFolderDelete}
      />

      {/* Upload Modal */}
      <Modal
        title="Upload RLC Methodology Documents"
        open={uploadModalOpen}
        onCancel={() => {
          setUploadModalOpen(false);
          uploadForm.resetFields();
          setUploadFiles([]);
        }}
        onOk={() => uploadForm.submit()}
        confirmLoading={uploading}
        width={700}
        okText={`Upload ${uploadFiles.length} file(s)`}
        okButtonProps={{ disabled: uploadFiles.length === 0 }}
      >
        <Form
          form={uploadForm}
          layout="vertical"
          onFinish={handleUpload}
        >
          <Form.Item
            label="Files"
            required
            help="Supported formats: PDF, PPTX, DOCX, TXT, MD (max 10MB each). Rate limit: 50 files per minute."
          >
            <Dragger
              multiple
              maxCount={50}
              beforeUpload={(file) => {
                // Validate file type
                const validTypes = [
                  'application/pdf',
                  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'text/plain',
                  'text/markdown',
                ];
                const validExtensions = ['.pdf', '.pptx', '.docx', '.txt', '.md'];
                const fileExt = '.' + file.name.split('.').pop().toLowerCase();

                if (!validTypes.includes(file.type) && !validExtensions.includes(fileExt)) {
                  message.error(`${file.name}: Invalid file type`);
                  return Upload.LIST_IGNORE;
                }

                // Validate file size (10MB)
                if (file.size > 10 * 1024 * 1024) {
                  message.error(`${file.name}: File must be smaller than 10MB`);
                  return Upload.LIST_IGNORE;
                }

                // Add to upload list
                setUploadFiles(prev => [...prev, {
                  file,
                  title: file.name.replace(/\.[^/.]+$/, '') // Default title from filename
                }]);

                return false; // Prevent auto upload
              }}
              onRemove={(file) => {
                setUploadFiles(prev => prev.filter(f => f.file.uid !== file.uid));
              }}
              fileList={uploadFiles.map(f => f.file)}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag files to this area to upload</p>
              <p className="ant-upload-hint">
                Support for single or bulk upload. Select up to 50 files at once.
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item
            name="folder_path"
            label="Folder Path"
            initialValue="/"
            rules={[
              { required: true, message: 'Please enter a folder path' },
              { pattern: /^\//, message: 'Folder path must start with /' }
            ]}
          >
            <Input placeholder="/Books or /Training/Videos" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            initialValue="methodology"
            rules={[
              { required: true, message: 'Please select a category' }
            ]}
            help="Category is required for embedding generation and semantic search filtering"
          >
            <Select
              placeholder="Select category"
              options={[
                { value: 'methodology', label: 'Methodology - Core RLC methodology content' },
                { value: 'knowledge_gap', label: 'Knowledge Gap - Knowledge gap related content' },
                { value: 'key_decision', label: 'Key Decision - Key decision related content' },
                { value: 'supporting_material', label: 'Supporting Material - Additional resources' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <TextArea
              placeholder="Optional description for all uploaded files"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags (Optional)"
            help="Select existing tags or type to create new ones"
          >
            <Select
              mode="tags"
              placeholder="Select or type tags"
              tokenSeparators={[',']}
              style={{ width: '100%' }}
              options={availableTags.map(tag => ({ label: tag, value: tag }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Document Details Drawer */}
      <Drawer
        title={selectedDocument?.title}
        placement="right"
        width="70%"
        onClose={() => {
          setDetailsDrawerOpen(false);
          setSelectedDocument(null);
          setIsEditingFilename(false);
          setDocumentUrl(null);
        }}
        open={detailsDrawerOpen}
        closable={true}
        extra={
          <Space>
            <Tooltip title="Download document">
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  if (selectedDocument) {
                    handleDownload(selectedDocument.id, selectedDocument.title);
                  }
                }}
              >
                Download
              </Button>
            </Tooltip>
          </Space>
        }
        styles={{
          body: { padding: 0, height: '100%' }
        }}
      >
        <div style={{ display: 'flex', height: '100%' }}>
          {/* Left Panel: Document Details */}
          <div
            style={{
              width: 350,
              borderRight: '1px solid var(--rlc-surface-muted)',
              overflowY: 'auto',
              padding: '16px'
            }}
          >
        {selectedDocument && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="File Name">
                {isEditingFilename ? (
                  <Space>
                    <Input
                      value={editedFilename}
                      onChange={(e) => setEditedFilename(e.target.value)}
                      style={{ width: 300 }}
                    />
                    <Button
                      size="small"
                      icon={<SaveOutlined />}
                      type="primary"
                      onClick={() => {
                        // TODO: Implement save filename
                        message.info('Filename editing not yet implemented');
                        setIsEditingFilename(false);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setIsEditingFilename(false)}
                    >
                      Cancel
                    </Button>
                  </Space>
                ) : (
                  <Space>
                    <span>{selectedDocument.filename}</span>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditedFilename(selectedDocument.filename);
                        setIsEditingFilename(true);
                      }}
                    >
                      Edit
                    </Button>
                  </Space>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Folder Path">
                <code>{selectedDocument.folder_path || '/'}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {selectedDocument.category || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Tags">
                {isEditingTags ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                      mode="tags"
                      value={editedTags}
                      onChange={setEditedTags}
                      placeholder="Select or type tags"
                      tokenSeparators={[',']}
                      style={{ width: '100%' }}
                      options={availableTags.map(tag => ({ label: tag, value: tag }))}
                    />
                    <Space>
                      <Button
                        size="small"
                        icon={<SaveOutlined />}
                        type="primary"
                        onClick={handleSaveTags}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setIsEditingTags(false)}
                      >
                        Cancel
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Space size="small" style={{ width: '100%' }}>
                    {selectedDocument.tags && selectedDocument.tags.length > 0 ? (
                      <Space size={[0, 4]} wrap>
                        {selectedDocument.tags.map((tag, index) => (
                          <Tag key={index} color="blue">{tag}</Tag>
                        ))}
                      </Space>
                    ) : (
                      <span>No tags</span>
                    )}
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditedTags(selectedDocument.tags || []);
                        setIsEditingTags(true);
                      }}
                    >
                      Edit
                    </Button>
                  </Space>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="File Type">
                {selectedDocument.file_type?.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="File Size">
                {selectedDocument.file_size_bytes
                  ? `${(selectedDocument.file_size_bytes / 1024).toFixed(2)} KB`
                  : 'N/A'
                }
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={
                  selectedDocument.status === 'active' ? 'green' :
                  selectedDocument.status === 'processing' ? 'blue' :
                  selectedDocument.status === 'failed' ? 'red' : 'default'
                }>
                  {selectedDocument.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Embeddings">
                {selectedDocument.embedding_count || 0} chunks
              </Descriptions.Item>
              {selectedDocument.page_count && (
                <Descriptions.Item label="Pages">
                  {selectedDocument.page_count}
                </Descriptions.Item>
              )}
              {selectedDocument.word_count && (
                <Descriptions.Item label="Word Count">
                  {selectedDocument.word_count.toLocaleString()}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Uploaded By">
                {selectedDocument.uploaded_by}
              </Descriptions.Item>
              <Descriptions.Item label="Uploaded At">
                {new Date(selectedDocument.uploaded_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Version">
                {selectedDocument.version}
              </Descriptions.Item>
              {selectedDocument.locked_by && (
                <Descriptions.Item label="Locked By">
                  {selectedDocument.locked_by} at {new Date(selectedDocument.locked_at).toLocaleString()}
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedDocument.description && (
              <>
                <Divider>Description</Divider>
                <p style={{ whiteSpace: 'pre-wrap' }}>{selectedDocument.description}</p>
              </>
            )}

            {selectedDocument.processing_error && (
              <>
                <Divider>Processing Error</Divider>
                <div style={{
                  padding: '12px',
                  background: 'var(--rlc-state-error-bg)',
                  border: '1px solid var(--rlc-state-error-bg)',
                  borderRadius: '4px',
                  color: 'var(--rlc-state-error)',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}>
                  {selectedDocument.processing_error}
                </div>
              </>
            )}

            <Divider>S3 Storage</Divider>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Bucket">
                {selectedDocument.source_s3_bucket || selectedDocument.s3_bucket}
              </Descriptions.Item>
              <Descriptions.Item label="Key">
                <code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                  {selectedDocument.source_s3_key || selectedDocument.s3_key}
                </code>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
          </div>

          {/* Right Panel: File Viewer */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--rlc-surface-subtle)' }}>
            {selectedDocument && documentUrl && (
              <>
                {selectedDocument.file_type === 'pdf' ? (
                  <PDFViewerCore
                    pdfUrl={documentUrl}
                    filename={selectedDocument.filename}
                    showToolbar={true}
                    toolbarPosition="top"
                    onDownload={() => handleDownload(selectedDocument.id, selectedDocument.title)}
                    containerStyle={{ height: '100%' }}
                  />
                ) : selectedDocument.file_type === 'pptx' || selectedDocument.file_type === 'ppt' ? (
                  <PPTXViewer
                    documentId={selectedDocument.id}
                    filename={selectedDocument.filename}
                    onDownload={() => handleDownload(selectedDocument.id, selectedDocument.title)}
                    containerStyle={{ height: '100%' }}
                    showToolbar={true}
                  />
                ) : selectedDocument.file_type === 'docx' || selectedDocument.file_type === 'doc' ? (
                  <OfficeDocumentViewer
                    fileUrl={documentUrl}
                    filename={selectedDocument.filename}
                    fileType={selectedDocument.file_type}
                    onDownload={() => handleDownload(selectedDocument.id, selectedDocument.title)}
                    containerStyle={{ height: '100%' }}
                  />
                ) : selectedDocument.file_type === 'txt' ? (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'white' }}>
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--rlc-surface-muted)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'var(--rlc-surface-subtle)'
                    }}>
                      <span style={{ fontWeight: 500 }}>{selectedDocument.filename}</span>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(selectedDocument.id, selectedDocument.title)}
                      >
                        Download
                      </Button>
                    </div>
                    <div style={{
                      flex: 1,
                      overflow: 'auto',
                      padding: '16px',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}>
                      {textContent || 'Loading text content...'}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '24px', textAlign: 'center' }}>
                    <p>Preview not available for this file type</p>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(selectedDocument.id, selectedDocument.title)}
                    >
                      Download to View
                    </Button>
                  </div>
                )}
              </>
            )}
            {loadingUrl && (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <p>Loading preview...</p>
              </div>
            )}
            {!loadingUrl && !documentUrl && selectedDocument && (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <p>Failed to load preview</p>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(selectedDocument.id, selectedDocument.title)}
                >
                  Download to View
                </Button>
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default ContentManagement;
